import {
  ComponentType,
  LazyExoticComponent,
  ReactNode,
  Suspense,
  lazy,
  type ComponentProps,
} from 'react';
import {Loading} from 'components/loading/Loading';
import {Route} from 'react-router-dom';
import type {ErrorMessage} from 'types/common';

/**
 * `KeysMatching` is a type that extracts the keys of an object that match a
 * given type.
 */
type KeysMatching<TObject, TValue> = {
  [TKey in keyof TObject]-?: TObject[TKey] extends TValue ? TKey : never;
}[keyof TObject];

/**
 * `NonEmpty` is a type that is the same as `T` if it is not `never` and
 * otherwise displays an error message.
 */
type NonEmpty<TObject> = [TObject] extends [never]
  ? ErrorMessage<'NonEmpty must receive a non-empty type'>
  : TObject;

type ComponentKeys<TModule> = NonEmpty<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  KeysMatching<TModule, ComponentType<any>>
>;

/**
 * Extracts the type of the component specified by a given key in a module.
 */
type ModuleComponent<TModule, TKey extends ComponentKeys<TModule>> = Extract<
  TModule[TKey],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  ComponentType<any>
>;

/**
 * Extracts the type of the props for a given component in a module.
 */
type ModuleComponentProps<
  TModule,
  TKey extends ComponentKeys<TModule>,
> = ComponentProps<ModuleComponent<TModule, TKey>>;

/**
 * Extracts the type of the default component in a module.
 */
type DefaultComponent<TModule> = Extract<
  TModule extends {default: infer TDefaultComponent}
    ? TDefaultComponent
    : never,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  ComponentType<any>
>;

/**
 * Extracts the type of the props for the default component in a module.
 */
type DefaultComponentProps<TModule> = ComponentProps<DefaultComponent<TModule>>;

/**
 * Lazy components keyed by call site, so that `buildLazyRoute` returns the
 * same component type every time a route tree is re-rendered. React treats a
 * fresh `lazy()` as a new component type: it unmounts the mounted page,
 * shows the Suspense fallback again, and restarts the page's data hooks. See
 * [#3574](https://github.com/CodeinPlace/cip/issues/3574) and
 * [#4819](https://github.com/CodeinPlace/cip/issues/4819).
 *
 * The key is the loader's source text plus the export it resolves. Loaders
 * are `() => import('...')` arrows, so the text identifies the module, and
 * two call sites that load the same export correctly share one component.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the cache holds every route's component; each caller's signature narrows the type it gets back
const lazyComponentCache = new Map<string, LazyExoticComponent<any>>();

/**
 * Returns the cached lazy component for `key`, creating it on first use.
 *
 * A `lazy()` remembers a failed import for good, so a component whose load
 * failed is dropped from the cache; the next render builds a fresh one and
 * retries, as a per-render `lazy()` always did.
 *
 * @param key Identifies the call site; see {@link lazyComponentCache}.
 * @param load Resolves the component when `key` has not been seen before.
 * @return The same component instance for every call with the same `key`,
 *     until its load fails.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const getOrCreateLazyComponent = <TComponent extends ComponentType<any>>({
  key,
  load,
}: {
  key: string;
  load: () => Promise<{default: TComponent}>;
}): LazyExoticComponent<TComponent> => {
  const cached = lazyComponentCache.get(key);
  if (cached) {
    return cached;
  }
  const created = lazy(async () => {
    try {
      return await load();
    } catch (error) {
      lazyComponentCache.delete(key);
      throw error;
    }
  });
  lazyComponentCache.set(key, created);
  return created;
};

/**
 * Lazy imports a named component from a module.
 * @throws an error if the component is not found in the module.
 */
const lazyImportNamedComponent = <
  TModule,
  TKey extends ComponentKeys<TModule>,
>({
  loader,
  name,
}: {
  loader: () => Promise<TModule>;
  name: TKey;
}): LazyExoticComponent<ModuleComponent<TModule, TKey>> => {
  return getOrCreateLazyComponent({
    key: `named:${String(name)}:${loader.toString()}`,
    load: async () => {
      const module = await loader();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      const component = (module as any)[name] as ModuleComponent<
        TModule,
        TKey
      >;
      if (!component)
        throw new Error(`Export "${String(name)}" not found in lazy module`);
      return {default: component};
    },
  });
};

/**
 * Lazy imports the default component from a module.
 * @throws an error if the default component is not found in the module.
 */
const lazyImportDefaultComponent = <TModule,>({
  loader,
}: {
  loader: () => Promise<TModule>;
}): LazyExoticComponent<DefaultComponent<TModule>> => {
  return getOrCreateLazyComponent({
    key: `default:${loader.toString()}`,
    load: async () => {
      const module = await loader();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      const component = (module as any).default as DefaultComponent<TModule>;
      if (!component)
        throw new Error('Module does not have a default React component');
      return {default: component};
    },
  });
};

/**
 * Base props for lazy routes.
 */
interface LazyRouteBaseProps<TModule> {
  /**
   * The fallback component to render while the route is loading. Defaults to
   * the Loading component.
   */
  fallback?: ReactNode;
  /**
   * The loader function for the route. Typically a dynamic import like
   * `() => import('./MyComponent')`.
   */
  loader: () => Promise<TModule>;
}

interface PathRouteProps {
  /**
   * The path of the route.
   */
  path: string;
}

interface IndexRouteProps {
  /**
   * Whether the route is an index route. Index routes are denoted by the
   * presence of the `index` property.
   */
  index: true;
}

/**
 * Props for lazy routes that specify a named component.
 */
type NamedRouteProps<
  TModule,
  TKey extends ComponentKeys<TModule>,
> = LazyRouteBaseProps<TModule> &
  (PathRouteProps | IndexRouteProps) & {
    /**
     * The name of the component to import from the module.
     */
    name: TKey;
    /**
     * The props to pass to the component.
     */
    componentProps?: ModuleComponentProps<TModule, TKey>;
  };

/**
 * Props for lazy routes that specify a default component.
 */
type DefaultRouteProps<TModule> = LazyRouteBaseProps<TModule> &
  (PathRouteProps | IndexRouteProps) & {
    /**
     * The props to pass to the component.
     */
    componentProps?: DefaultComponentProps<TModule>;
  };

/**
 * Overload to specify a named component.
 */
export function buildLazyRoute<TModule, TKey extends ComponentKeys<TModule>>(
  props: NamedRouteProps<TModule, TKey>,
): ReturnType<typeof Route>;

/**
 * Overload to specify a default component.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export function buildLazyRoute<TModule extends {default: ComponentType<any>}>(
  props: DefaultRouteProps<TModule>,
): ReturnType<typeof Route>;

/** Implementation — strongly typed union + in-branch render */
export function buildLazyRoute<TModule, TKey extends ComponentKeys<TModule>>(
  props: NamedRouteProps<TModule, TKey> | DefaultRouteProps<TModule>,
): ReturnType<typeof Route> {
  const {fallback, loader} = props;

  // If "name" is in the props, we are specifying a named component.
  if ('name' in props) {
    const Component = lazyImportNamedComponent<TModule, typeof props.name>({
      loader,
      name: props.name,
    });
    const element = (
      <Suspense fallback={fallback ?? <Loading />}>
        <Component {...props.componentProps} />
      </Suspense>
    );
    if ('index' in props) {
      return <Route index={true} element={element} />;
    } else {
      return <Route path={props.path} element={element} />;
    }
  } else {
    // Default component path
    const Component = lazyImportDefaultComponent<TModule>({loader});
    const element = (
      <Suspense fallback={fallback ?? <Loading />}>
        <Component {...props.componentProps} />
      </Suspense>
    );
    if ('index' in props) {
      return <Route index={true} element={element} />;
    } else {
      return <Route path={props.path} element={element} />;
    }
  }
}
